/*
 * Copyright (C) 2025 Musarubra US LLC.  All Rights Reserved.
 */

import { WidgetContext } from './widget-context-base';
import { iamLogin, iamUtils } from '../main';
import { WidgetElements } from '../elements/widget-elements-base';
import { iam } from '../iam';

export class MfaVerifyWidgetContext extends WidgetContext {
  private currentFactor: string;
  /* keep track of the observer so we never initialize multiple observers */
  private oktaHeaderBeaconObserver: MutationObserver = null;
  /* grabbed and saved from page for reusability */
  private mfaDropdownFieldset: HTMLElement = null;

  constructor(elements: WidgetElements) {
    super(elements);
  }

  public override initialize() {
    // login.html's mfa verification screen (password entry was successful, now the user needs to enter an mfa code)
    if (iam.settings.debug) console.log('initializing mfa verify widget');
    document.getElementById('dont-have-a-mvision').style.display = 'none';
    this.currentFactor = this.elements.getMfaFactor();
    if (this.currentFactor === 'factor-totp') {
      // for otp we use our own generic otp text rather than the widget's google auth text
      iamLogin.registerLocalizationCallback(() => {
        iamLogin.localizeElement('sub-header', 'factor-totp-auth');
        iamLogin.localizeElement('sub-header-2', 'otpSubhead2');
      }, true, true);
    } else {
      // pull text from okta's hidden title and subtitle and stick them in our sub header elements
      document.getElementById('sub-header').textContent = this.elements.getFormHead().textContent;
      if (this.elements.getFormSubtitle()) {
        document.getElementById('sub-header-2').textContent = this.elements.getFormSubtitle().textContent;
      }
    }
    if (!this.oktaHeaderBeaconObserver) {
      // if user is enrolled in more than one mfa option, need the dropdown for choosing mfa type
      // unfortunately the dropdown doesn't appear instantly so we have to watch for it with a mutation observer,
      // then extract the menu item labels and clickable elements to use in our own dropdown.
      const initializeFactorDropdown = (oktaMfaDropdown: HTMLElement) => {
        const menuItems = oktaMfaDropdown.querySelectorAll<HTMLElement>('.okta-dropdown-list li:not(.dropdown-list-title)');
        if (menuItems.length > 1) {
          if (iam.settings.debug) console.log('factor dropdown added by okta');
          // configure our dropdown to parallel the hidden okta dropdown
          const fieldset = this.mfaDropdownFieldset || this.elements.getMultiMfaDropdownFieldset();
          const selectedFactor = fieldset.querySelector('#selectedFactor');
          // set the text of the main control to our "select authentication" method text
          iamLogin.registerLocalizationCallback(() => {
            iamLogin.localizeElement(selectedFactor, this.currentFactor);
          }, true, true);
          const dropdownBar = fieldset.querySelector<HTMLElement>('.factorDropdown > div');
          const menu = fieldset.querySelector<HTMLElement>('.factorDropdown > menu');
          if (!this.mfaDropdownFieldset) {
            // first time initialization
            if (iam.settings.debug) console.log('initializing factor dropdown');
            dropdownBar.tabIndex = 0;
            // by saving off the fieldset in a global like this, we can reuse this even if an widget transition causes it to be dropped from the DOM
            this.mfaDropdownFieldset = fieldset;
            // localize label "Select Authentication Method"
            iamLogin.registerLocalizationCallback(() => {
              iamLogin.localizeElement(fieldset.querySelector('#factorLabel'), 'chooseFactorLabel');
            }, true, true);
            // handle clicks on the dropdown bar to open and close, and while open watch whole page for clicks to close it
            let bodyClickHandler: (e: MouseEvent) => void;
            dropdownBar.addEventListener('click', () => {
              const open = dropdownBar.getAttribute('open') !== 'true';
              dropdownBar.setAttribute('open', open ? 'true' : 'false');
              menu.style.display = open ? 'block' : 'none';
              if (open) {
                if (!bodyClickHandler) {
                  bodyClickHandler = e => {
                    if (e.target instanceof Element && e.target.closest('.factorDropdown') === null) {
                      dropdownBar.setAttribute('open', 'false');
                      menu.style.display = 'none';
                    }
                  };
                  document.body.addEventListener('click', bodyClickHandler);
                }
                fieldset.querySelector('.factorDropdown menu ul li.focused')?.classList.remove('focused');
              } else {
                if (bodyClickHandler) {
                  document.body.removeEventListener('click', bodyClickHandler);
                  bodyClickHandler = null;
                }
              }
            });
            dropdownBar.addEventListener('keydown', (e: KeyboardEvent) => {
              switch (e.key) {
                case 'Enter':
                  // toggle open state
                  if (dropdownBar.getAttribute('open') === 'true') {
                    // if a list item was selected via keyboard, click it instead of the bar
                    const focused = fieldset.querySelector<HTMLElement>('.factorDropdown menu ul li.focused a');
                    if (focused) {
                      focused.click();
                    } else {
                      dropdownBar.click();
                    }
                  } else {
                    dropdownBar.click();
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  break;
                case ' ': // open
                  if (dropdownBar.getAttribute('open') !== 'true') {
                    dropdownBar.click();
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  break;
                case 'Escape': // close
                  if (dropdownBar.getAttribute('open') === 'true') {
                    dropdownBar.click();
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  break;
                case 'ArrowUp':
                  if (dropdownBar.getAttribute('open') === 'true') {
                    const list = fieldset.querySelector('.factorDropdown menu ul');
                    const children = Array.from(list.children) as HTMLElement[];
                    const prev = list.querySelector<HTMLElement>('li.focused');
                    const index = prev ? children.indexOf(prev) : -1;
                    const next = children[Math.max(0, index - 1)];
                    prev?.classList.remove('focused');
                    next.classList.add('focused');
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  break;
                case 'ArrowDown':
                  if (dropdownBar.getAttribute('open') === 'true') {
                    const list = fieldset.querySelector('.factorDropdown menu ul');
                    const children = Array.from(list.children) as HTMLElement[];
                    const prev = list.querySelector<HTMLElement>('li.focused');
                    const index = prev ? children.indexOf(prev) : -1;
                    const next = children[Math.min(index + 1, list.children.length - 1)];
                    prev?.classList.remove('focused');
                    next.classList.add('focused');
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  break;
                case 'Tab':
                  if (dropdownBar.getAttribute('open') === 'true') {
                    // close and then let the browser handle the focus change
                    // don't handle this via a 'blur' handler because that will interfere with mouse clicks in the dropdown!
                    dropdownBar.click();
                  }
                  break;
              }
            });
          }

          const ul = fieldset.querySelector('.factorDropdown menu ul');
          ul.innerHTML = '';
          // make menu items that match okta's menu items, except for the first one which is just a title
          menuItems.forEach(item => {
            if (iam.settings.debug) console.log('adding factor menu item', item);
            const li = document.createElement('li');
            li.className = 'dropdown-content-item';
            const anchor = document.createElement('a');
            const mfaClassName = item.querySelector('[class*=mfa-]')?.className.split(' ').find(c => c.startsWith('mfa-'));
            if (mfaClassName) {
              // change the dropdown list item text to our "select authentication" method text
              const factor = /^mfa-(.*?)(-[0-9]+)?$/.exec(mfaClassName)[1]; // the class name which tells us what method it is looks like "mfa-sms-30"
              iamLogin.registerLocalizationCallback(() => {
                iamLogin.localizeElement(anchor, `factor-${factor}`);
              }, true, true);
            } else {
              console.error('unable to find factor from menu item', item);
              // fall back to whatever is in the okta element (should never happen)
              anchor.textContent = item.textContent;
            }
            anchor.addEventListener('click', () => {
              // do nothing except close the menu if this is the one that's already selected
              if (anchor.textContent !== fieldset.querySelector('#selectedFactor').textContent) {
                // click the list item in okta's hidden dropdown list, so okta's widget will do what it needs to do
                if (iam.settings.debug) console.log('clicking menu item', item);
                item.click();
              }
              dropdownBar.setAttribute('open', 'false');
              menu.style.display = 'none';
            });
            li.appendChild(anchor);
            ul.appendChild(li);
          });

          // now inject the dropdown fieldset at the beginning of the input fields
          this.elements.getFieldsetContainer().insertBefore(fieldset, this.elements.getFieldsetContainer().firstChild);
          fieldset.style.display = '';
        } else {
          if (iam.settings.debug) console.log('factor dropdown added by okta is empty');
        }
      };
      this.oktaHeaderBeaconObserver = new MutationObserver((mutationList, observer) => {
        for (let i = 0; i < mutationList.length; i++) {
          for (let j = 0; j < mutationList[i].addedNodes.length; j++) {
            // okta's "header beacon", which we hide, is changing, and that's where okta's multi-mfa dropdown is.
            // if the multi-mfa dropdown was just added, we need to populate and show our dropdown to parallel theirs.
            if (mutationList[i].addedNodes[j] instanceof Element) {
              const oktaMfaDropdown = (mutationList[i].addedNodes[j] as Element).querySelector<HTMLElement>('[data-type=factor-types-dropdown]');
              if (oktaMfaDropdown) {
                initializeFactorDropdown(oktaMfaDropdown);
              }
            }
          }
        }
      });
      this.oktaHeaderBeaconObserver.observe(this.elements.getHeaderBeacon(), { childList: true });
      if (iam.settings.debug) console.info('observing okta header beacon');
      const oktaMfaDropdown = this.elements.getMultiMfaDropdown();
      if (oktaMfaDropdown) {
        if (iam.settings.debug) console.info('factor dropdown already exists');
        initializeFactorDropdown(oktaMfaDropdown);
      }
    }
    // show footer and 2nd sub header
    iamLogin.page.showCommonFields(['post-okta', 'sub-header-2']);
    iamLogin.page.initializeCommon();
  }

  public override setInitialFocus() {
    let field;
    // this context covers both sms and phone call depending on what the account was configured for
    if (this.elements.getSmsRequestButtons()?.length) {
      field = this.elements.getSmsRequestButtons()[0];
    } else if (this.elements.getCallRequestButtons()?.length) {
      field = this.elements.getCallRequestButtons()[0];
    }
    if (field && !field.disabled) {
      field.focus();
    }
  }

  public override validateForm() {
    // disable submit button if an mfa code hasn't been entered
    this.elements.getSubmitButton().disabled = this.elements.getAnswerField() && this.elements.getAnswerField().value.length === 0;
  }

  public override hasContextMutated() {
    // has the user chosen a different mfa factor to authenticate this time with?
    return this.currentFactor && this.currentFactor !== this.elements.getMfaFactor();
  }
}
